import dayjs from "dayjs";
import { Typography } from "@mui/joy";
import {
  IonCard,
  IonCardContent,
  IonCol,
  IonGrid,
  IonIcon,
  IonItem,
  IonLabel,
  IonRow,
} from "@ionic/react";
import { callSharp, logoWhatsapp, homeOutline } from "ionicons/icons";
import ModernDesplegable from "../../components/basicsComponents/ModernDesplegable/ModernDesplegable";
import StyledButton from "../../components/basicsComponents/StyledButton/StyledButton";
import CustomToast from "../../components/basicsComponents/CustomToast/CustomToast";
import { DialogoConfirmacion } from "../../components/basicsComponents/DialogoConfirmacion/DialogoConfirmacion";
import LoadingBackdrop from "../../components/basicsComponents/LoadingBackdrop/LoadingBackdrop";
import {
  Column,
  Container,
  Row,
} from "../../components/basicsComponents/MaterialGridComponents";
import CustomCalendar from "../../components/basicsComponents/Calendar";
import {
  obtenerNombreDia,
  convertToPlain,
} from "../../utils/funcionalidadesStrings";
import useTituloPagina from "../../hooks/tituloPagina";
import { useNuevoTurnoImagenes } from "./useNuevoTurnoImagenes";
import "../../theme/turno-button.css";
import "./NuevoTurnoConfirmacionModal.css";

import React, { useState, useEffect } from "react";
import "../../theme/turno-rango-button.css";
import { useHorariosPrestador } from "./useHorariosPrestador";

// Estilos para el spinner
const spinnerStyles = `
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

function NuevoTurnoImagenes() {
  useTituloPagina();
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastColor, setToastColor] = useState("");

  const mostrarNotificacion = (open, mensaje, color) => {
    setOpenToast(open);
    setToastMessage(mensaje);
    setToastColor(color);
  };

  const {
    usuario,
    urlAxioPP,
    cargando,
    cargandoTurnos,
    estudios,
    estudiosDesplegable,
    estudioSeleccionado,
    fechasFuturas,
    fechaSeleccionada,
    turnosDisponibles,
    turnosPublicados,
    turnoSeleccionado,
    mostrarInasisEstudio,
    mostrarTurnoPrestadorReservado,
    abrirDesplegableEstudios,
    abrirModalConfirmarReserva,
    abrirModalPostReserva,
    setAbrirDesplegableEstudios,
    setAbrirModalConfirmarReserva,
    setAbrirModalPostReserva,
    handleChangeSelect,
    handleChangeFecha,
    handleClickSeleccionarTurno,
    handleClickConfirmarHacerReserva,
    handleClickCopiarTelefono,
    handlePostTurnoReservado,
    renderTurnos,
    getPrestadorSeleccionadoData,
    getEstudioSeleccionadoData,
  } = useNuevoTurnoImagenes(mostrarNotificacion);

  const prestadorData = getPrestadorSeleccionadoData();
  const estudioData = getEstudioSeleccionadoData();

  // Memo de horarios combinados (publicados + disponibles)
  const [horariosMerged, setHorariosMerged] = useState([]);

  // Horarios declarados del prestador/estudio (rango configurado)
  const estudioCodigoConMutual =
    estudioSeleccionado !== -1 && usuario?.mutual
      ? `${estudioSeleccionado}-${usuario.mutual}`
      : null;
  const { horariosPrestador, cargandoHorarios } = useHorariosPrestador(
    estudioCodigoConMutual,
    usuario,
    urlAxioPP || process.env.REACT_APP_API_URL,
  );

  // Extrae rango "De HH:mm a HH:mm" -> slots de 15m
  const getRangoHorariosPrestador = () => {
    if (!horariosPrestador || !fechaSeleccionada) return [];
    const diasMap = {
      domingo: "domingo",
      lunes: "lunes",
      martes: "martes",
      miércoles: "miercoles",
      miercoles: "miercoles",
      jueves: "jueves",
      viernes: "viernes",
      sábado: "sabado",
      sabado: "sabado",
      sunday: "domingo",
      monday: "lunes",
      tuesday: "martes",
      wednesday: "miercoles",
      thursday: "jueves",
      friday: "viernes",
      saturday: "sabado",
    };
    const diaSemana = dayjs(fechaSeleccionada)
      .locale("es")
      .format("dddd")
      .toLowerCase();
    const diaKey = diasMap[diaSemana] || diaSemana;
    const bloques = [`${diaKey}M`, `${diaKey}T`];
    let horarios = [];
    bloques.forEach((b) => {
      const franja = horariosPrestador[b];
      if (
        franja &&
        typeof franja === "string" &&
        franja.includes("De") &&
        franja.includes("a")
      ) {
        const match = franja.match(/De (\d{2}:\d{2}) a (\d{2}:\d{2})/);
        if (match) {
          let h = dayjs(match[1], "HH:mm");
          const hHasta = dayjs(match[2], "HH:mm");
          while (h.isBefore(hHasta) || h.isSame(hHasta)) {
            horarios.push(h.format("HH:mm"));
            h = h.add(15, "minute");
          }
        }
      }
    });
    return horarios;
  };

  useEffect(() => {
    if (!fechaSeleccionada) {
      setHorariosMerged([]);
      return;
    }

    const normalize = (h) => {
      const d = dayjs(h, "HH:mm", true);
      return d.isValid() ? d.format("HH:mm") : null;
    };

    const horariosDisponiblesFecha = turnosDisponibles
      .filter(
        (t) =>
          dayjs(t.fecha).format("YYYY-MM-DD") ===
          dayjs(fechaSeleccionada).format("YYYY-MM-DD"),
      )
      .map((t) => normalize(dayjs(t.hora).format("HH:mm")))
      .filter(Boolean);

    const horariosPublicadosFecha = Array.from(
      new Set(
        (turnosPublicados || [])
          .filter(
            (t) =>
              dayjs(t.fecha).format("YYYY-MM-DD") ===
              dayjs(fechaSeleccionada).format("YYYY-MM-DD"),
          )
          .map((t) => normalize(dayjs(t.hora).format("HH:mm")))
          .filter(Boolean),
      ),
    );

    const rangoConfigurado = getRangoHorariosPrestador()
      .map(normalize)
      .filter(Boolean);

    const baseHorarios =
      rangoConfigurado.length > 0 ? rangoConfigurado : horariosPublicadosFecha;

    const merged = Array.from(
      new Set([...baseHorarios, ...horariosDisponiblesFecha]),
    ).sort((a, b) => dayjs(a, "HH:mm").valueOf() - dayjs(b, "HH:mm").valueOf());

    if (merged.length === 0) {
      if (cargandoHorarios) {
        setHorariosMerged(["__loading"]);
        return;
      }
      setHorariosMerged(["__sin-horarios"]);
      return;
    }

    setHorariosMerged(merged);
  }, [
    turnosDisponibles,
    turnosPublicados,
    fechaSeleccionada,
    horariosPrestador,
    cargandoHorarios,
  ]);

  const handleVolverInicio = () => {
    window.location.href = "/portal/Inicio";
  };

  return (
    <>
      <style>{spinnerStyles}</style>
      <div slot="content">
        <Container>
          {/* Header con título y botón volver */}
          <Row
            container
            width="100%"
            style={{ marginTop: "24px", marginBottom: "16px" }}
          >
            <Column xs={12}>
              <div
                style={{
                  display: "flex",
                  flexDirection: window.innerWidth < 768 ? "column" : "row",
                  alignItems: window.innerWidth < 768 ? "stretch" : "center",
                  justifyContent: "space-between",
                  gap: "12px",
                  padding: "0 16px",
                }}
              >
                <Typography
                  level="h2"
                  sx={{
                    fontSize: { xs: "1.5rem", md: "1.875rem" },
                    fontWeight: 700,
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Reserva de Turnos - Estudios
                </Typography>
                <button
                  onClick={handleVolverInicio}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    padding: "12px 20px",
                    backgroundColor: "white",
                    border: "2px solid #667eea",
                    borderRadius: "12px",
                    color: "#667eea",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    boxShadow: "0 2px 8px rgba(102, 126, 234, 0.10)",
                    minWidth: "fit-content",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#667eea";
                    e.target.style.color = "white";
                    e.target.style.transform = "translateY(-2px)";
                    e.target.style.boxShadow =
                      "0 4px 12px rgba(102, 126, 234, 0.18)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "white";
                    e.target.style.color = "#667eea";
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow =
                      "0 2px 8px rgba(102, 126, 234, 0.10)";
                  }}
                >
                  <IonIcon icon={homeOutline} style={{ fontSize: "18px" }} />
                  <span>Volver al inicio</span>
                </button>
              </div>
            </Column>
          </Row>

          {/* Desplegable de estudios */}
          <Row
            container
            width="100%"
            style={{ marginTop: "16px", marginBottom: "16px" }}
          >
            <Column xs={12} md={6}>
              <IonItem
                className="ion-item-no-border"
                style={{ width: "100%", maxWidth: "480px", margin: "0 auto" }}
              >
                <ModernDesplegable
                  array={estudiosDesplegable}
                  value={estudioSeleccionado}
                  handleChange={handleChangeSelect}
                  placeholder="Seleccione un estudio..."
                  label="Estudios"
                  id="Estudio"
                  isOpen={abrirDesplegableEstudios}
                  setIsOpen={setAbrirDesplegableEstudios}
                  disabled={cargando}
                  emoji="🔬"
                  color="#667eea"
                />
              </IonItem>
            </Column>
          </Row>

          {/* Mensaje de preparación para el estudio */}
          {estudioSeleccionado !== -1 &&
            estudioData &&
            estudioData.preparacion &&
            estudioData.preparacion !== "" && (
              <Row
                container
                width="100%"
                style={{ marginTop: "16px", marginBottom: "16px" }}
              >
                <Column xs={12}>
                  <IonCard
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, rgba(245, 158, 11, 0.05) 100%)",
                      border: "2px solid #f59e0b",
                      borderRadius: "16px",
                      boxShadow: "0 4px 12px rgba(245, 158, 11, 0.15)",
                      margin: "0 16px",
                    }}
                  >
                    <IonCardContent style={{ padding: "20px" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "12px",
                        }}
                      >
                        <span style={{ fontSize: "28px", flexShrink: 0 }}>
                          ⚠️
                        </span>
                        <div style={{ flex: 1 }}>
                          <Typography
                            level="h4"
                            sx={{
                              fontWeight: 700,
                              fontSize: "18px",
                              color: "#b45309",
                              marginBottom: "8px",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                            }}
                          >
                            PREPARACIÓN REQUERIDA
                          </Typography>
                          <Typography
                            level="body-md"
                            sx={{
                              color: "#78350f",
                              fontSize: "15px",
                              lineHeight: "1.6",
                              whiteSpace: "pre-wrap",
                              fontWeight: 500,
                            }}
                          >
                            {convertToPlain(estudioData.preparacion)}
                          </Typography>
                        </div>
                      </div>
                    </IonCardContent>
                  </IonCard>
                </Column>
              </Row>
            )}
        </Container>
        {cargandoTurnos && (
          <IonGrid>
            <IonRow
              className="ion-justify-content-center"
              style={{ marginTop: "40px" }}
            >
              <IonCol size="12" sizeMd="6" style={{ textAlign: "center" }}>
                <div
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)",
                    border: "2px solid #667eea",
                    borderRadius: "16px",
                    padding: "24px",
                    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.1)",
                  }}
                >
                  <div
                    className="spinner"
                    style={{
                      margin: "0 auto 16px",
                      width: "48px",
                      height: "48px",
                      border: "4px solid rgba(102, 126, 234, 0.1)",
                      borderTopColor: "#667eea",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                    }}
                  ></div>
                  <Typography
                    level="h4"
                    sx={{
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      fontWeight: 700,
                      fontSize: "18px",
                      marginBottom: "8px",
                    }}
                  >
                    Procesando...
                  </Typography>
                  <Typography
                    level="body-sm"
                    sx={{ color: "#6b7280", fontSize: "14px" }}
                  >
                    Cargando turnos disponibles para el estudio seleccionado
                  </Typography>
                </div>
              </IonCol>
            </IonRow>
          </IonGrid>
        )}
        {fechasFuturas.length > 0 && (
          <IonGrid style={{ padding: "0 8px" }}>
            <IonRow
              style={{
                gap: "12px",
                justifyContent: "center",
                alignItems: "flex-start",
              }}
            >
              <IonCol
                size="12"
                sizeMd="5"
                sizeLg="5"
                style={{ maxWidth: "400px", padding: "0 4px" }}
              >
                <div
                  style={{
                    background: "white",
                    border: "1.5px solid #d6ddff",
                    borderRadius: "14px",
                    padding: "12px",
                    boxShadow: "0 8px 22px rgba(110, 128, 255, 0.08)",
                  }}
                >
                  <CustomCalendar
                    onChange={(newSelectedDate) =>
                      handleChangeFecha(
                        dayjs(newSelectedDate).format("YYYY-MM-DD"),
                      )
                    }
                    minDate={dayjs(new dayjs())}
                    maxDate={dayjs(new dayjs()).add(3, "months")}
                    value={fechaSeleccionada}
                    highlightedDates={fechasFuturas}
                  />
                </div>
              </IonCol>
              {(horariosMerged.length > 0 || cargandoTurnos) && (
                <IonCol
                  size="12"
                  sizeMd="5"
                  sizeLg="5"
                  style={{ maxWidth: "420px", padding: "0 4px" }}
                >
                  {/* ESTILO CON DECORACIÓN (Actual) */}
                  <div
                    style={{
                      background: "white",
                      border: "1.5px solid #d6ddff",
                      borderRadius: "14px",
                      padding: "12px",
                      boxShadow: "0 8px 22px rgba(110, 128, 255, 0.08)",
                      maxWidth: "100%",
                    }}
                  >
                    <div
                      style={{
                        background: "#f5f7ff",
                        border: "1px solid #e5e8ff",
                        borderRadius: "12px",
                        padding: "10px",
                        marginBottom: "10px",
                        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.8)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginBottom: "6px",
                        }}
                      >
                        <span style={{ fontSize: "20px" }}>📅</span>
                        <Typography
                          level="h4"
                          sx={{
                            color: "#3c4b82",
                            fontWeight: 800,
                            fontSize: "16px",
                            margin: 0,
                            textTransform: "capitalize",
                          }}
                        >
                          {`${obtenerNombreDia(
                            dayjs(fechaSeleccionada).format("dddd"),
                          )} ${dayjs(fechaSeleccionada).format("DD")} de ${dayjs(
                            fechaSeleccionada,
                          ).format("MMMM, YYYY")}`}
                        </Typography>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <span style={{ fontSize: "14px" }}>⏰</span>
                        <Typography
                          level="body-sm"
                          sx={{
                            color: "#6b7280",
                            fontWeight: 600,
                            fontSize: "13px",
                          }}
                        >
                          Horarios disponibles
                        </Typography>
                      </div>
                    </div>
                    <div
                      style={{
                        background: "#f9faff",
                        border: "1px solid #e5e8ff",
                        borderRadius: "12px",
                        padding: "8px",
                      }}
                    >
                      <IonGrid>
                        <IonRow style={{ flexWrap: "wrap", gap: "4px" }}>
                          {cargandoTurnos && horariosMerged.length === 0 && (
                            <Typography
                              level="body-sm"
                              sx={{ color: "#6b7280", padding: "4px 8px" }}
                            >
                              Cargando horarios...
                            </Typography>
                          )}
                          {horariosMerged.map((hora, idx) => {
                            if (hora === "__sin-horarios") {
                              return (
                                <Typography
                                  key="sin-horarios"
                                  level="body-sm"
                                  sx={{ color: "#6b7280", padding: "4px 8px" }}
                                >
                                  Sin horarios configurados para este día
                                </Typography>
                              );
                            }

                            const esDisponible = turnosDisponibles.some(
                              (t) =>
                                dayjs(t.fecha).format("YYYY-MM-DD") ===
                                  dayjs(fechaSeleccionada).format(
                                    "YYYY-MM-DD",
                                  ) && dayjs(t.hora).format("HH:mm") === hora,
                            );

                            if (esDisponible) {
                              return (
                                <button
                                  key={`disp-${hora}-${idx}`}
                                  onClick={() => {
                                    const turno = turnosDisponibles.find(
                                      (t) =>
                                        dayjs(t.fecha).format("YYYY-MM-DD") ===
                                          dayjs(fechaSeleccionada).format(
                                            "YYYY-MM-DD",
                                          ) &&
                                        dayjs(t.hora).format("HH:mm") === hora,
                                    );
                                    if (turno)
                                      handleClickSeleccionarTurno(turno);
                                  }}
                                  className="turno-button"
                                  style={{
                                    padding: "8px 14px",
                                    fontSize: "0.95rem",
                                    borderRadius: "12px",
                                    margin: "0 6px 8px 0",
                                  }}
                                >
                                  {hora}
                                </button>
                              );
                            }

                            return (
                              <button
                                key={`nd-${hora}-${idx}`}
                                className="turno-rango-button"
                                tabIndex={-1}
                                disabled
                              >
                                {hora}
                              </button>
                            );
                          })}
                        </IonRow>
                      </IonGrid>
                    </div>
                  </div>
                </IonCol>
              )}
            </IonRow>
          </IonGrid>
        )}
      </div>
      {mostrarInasisEstudio && (
        <IonRow className="ion-justify-content-center">
          <IonCol>
            <IonCard color="warning">
              <IonCardContent>
                <h4 className="main-content">
                  ⚠ No podes reservar un nuevo turno con este estudio debido a
                  que no asististe a 2 o más turnos en los últimos 2 meses para
                  el estudio que seleccionaste. Estaremos encantados de
                  atenderte y brindarte la mejor atención personalizada si te
                  pones en contacto. Podes presionar los botones inferiores para
                  copiar el número de teléfono fijo o llevarte a nuestro chat de
                  Whatsapp.
                </h4>
              </IonCardContent>
              <IonRow>
                <IonCol>
                  <StyledButton className="verde" expand="block">
                    <IonIcon icon={logoWhatsapp} />
                    <IonLabel>WhatsApp</IonLabel>
                  </StyledButton>
                </IonCol>
                <IonCol>
                  <StyledButton
                    className="blanco"
                    expand="block"
                    onClick={handleClickCopiarTelefono}
                  >
                    <IonIcon icon={callSharp} />
                    <IonLabel>Teléfono</IonLabel>
                  </StyledButton>
                </IonCol>
              </IonRow>
            </IonCard>
          </IonCol>
        </IonRow>
      )}
      {mostrarTurnoPrestadorReservado && (
        <IonRow className="ion-justify-content-center">
          <IonCol>
            <IonCard color="warning">
              <IonCardContent>
                <h4 className="main-content">
                  ⚠ No está permitido sacar un segundo turno para un mismo
                  prestador
                </h4>
              </IonCardContent>
            </IonCard>
          </IonCol>
        </IonRow>
      )}
      {estudioSeleccionado !== -1 && (
        <DialogoConfirmacion
          titulo="Reservar turno"
          open={abrirModalConfirmarReserva}
          setOpen={setAbrirModalConfirmarReserva}
          handleClickConfirmar={handleClickConfirmarHacerReserva}
          colorBotonNo="danger"
          colorBotonSi="success"
          textoBotonNo="No, Revisar"
          textoBotonSi="Si, Confirmar"
          variantDialog="soft"
          contenido={
            <div className="nt-confirm-modal">
              <div className="nt-confirm-header">
                <span className="nt-confirm-title">Resumen del turno</span>
                <span className="nt-confirm-chip">Estudio</span>
              </div>
              <div className="nt-confirm-grid">
                <div className="nt-confirm-field">
                  <span className="nt-confirm-label">Estudio</span>
                  <span className="nt-confirm-value">
                    {turnoSeleccionado.estudioNom ||
                      estudios.find((e) => e.codigo === estudioSeleccionado)
                        ?.nombre ||
                      "-"}
                  </span>
                </div>
                <div className="nt-confirm-field">
                  <span className="nt-confirm-label">Fecha</span>
                  <span className="nt-confirm-value">
                    {turnoSeleccionado.fecha
                      ? dayjs(turnoSeleccionado.fecha).format("DD/MM/YYYY")
                      : "-"}
                  </span>
                </div>
                <div className="nt-confirm-field">
                  <span className="nt-confirm-label">Hora</span>
                  <span className="nt-confirm-value">
                    {turnoSeleccionado.hora
                      ? dayjs(turnoSeleccionado.hora).format("HH:mm")
                      : "-"}
                  </span>
                </div>
                <div className="nt-confirm-field">
                  <span className="nt-confirm-label">Tipo de atención</span>
                  <span className="nt-confirm-value">
                    {usuario?.mutualNombre || "Obra social"}
                  </span>
                </div>
              </div>
              <div className="nt-confirm-section">
                <span className="nt-confirm-section-title">
                  Datos del paciente
                </span>
                <div className="nt-confirm-grid">
                  <div className="nt-confirm-field">
                    <span className="nt-confirm-label">Nombre</span>
                    <span className="nt-confirm-value">
                      {usuario?.nombre || "-"}
                    </span>
                  </div>
                  <div className="nt-confirm-field">
                    <span className="nt-confirm-label">Apellido</span>
                    <span className="nt-confirm-value">
                      {usuario?.apellido || "-"}
                    </span>
                  </div>
                  <div className="nt-confirm-field">
                    <span className="nt-confirm-label">Documento</span>
                    <span className="nt-confirm-value">
                      {usuario?.documentoNro || "-"}
                    </span>
                  </div>
                  <div className="nt-confirm-field">
                    <span className="nt-confirm-label">Obra social</span>
                    <span className="nt-confirm-value">
                      {usuario?.mutualNombre || "-"}
                    </span>
                  </div>
                  <div className="nt-confirm-field">
                    <span className="nt-confirm-label">Nro. afiliado</span>
                    <span className="nt-confirm-value">
                      {usuario?.mutualAfiliado || "-"}
                    </span>
                  </div>
                  <div className="nt-confirm-field">
                    <span className="nt-confirm-label">Celular</span>
                    <span className="nt-confirm-value">
                      {usuario?.celular || "-"}
                    </span>
                  </div>
                  <div className="nt-confirm-field">
                    <span className="nt-confirm-label">Correo</span>
                    <span className="nt-confirm-value">
                      {usuario?.email || "-"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="nt-confirm-note">
                Si algún dato no es correcto, presioná “No, Revisar”.
              </div>
            </div>
          }
        />
      )}
      {estudioSeleccionado !== -1 && (
        <DialogoConfirmacion
          titulo="Turno reservado"
          texto={`Se ha reservado exitosamente el turno para el estudio ${turnoSeleccionado.estudioNom || estudios.find((e) => e.codigo === estudioSeleccionado)?.nombre || ""}, para el día ${dayjs(turnoSeleccionado.fecha).format("DD/MM/YYYY")}, a las ${dayjs(turnoSeleccionado.hora).format("HH:mm")}`}
          open={abrirModalPostReserva}
          setOpen={setAbrirModalPostReserva}
          handleClickConfirmar={() => handlePostTurnoReservado("nuevoTurno")}
          handleClickCancelar={() => handlePostTurnoReservado("turnosPaciente")}
          variantBotonNo="solid"
          colorBotonNo="primary"
          variantBotonSi="solid"
          colorBotonSi="primary"
          variantDialog="plain"
          colorDialogo="success"
          textoBotonNo="Consultar mis turnos"
          textoBotonSi="Reservar nuevo turno"
        />
      )}
      {cargando && <LoadingBackdrop visualizar={cargando} />}
      <CustomToast
        openToast={openToast}
        onDidDismiss={() => setOpenToast(false)}
        message={toastMessage}
        colorNotificacion={toastColor}
      />
    </>
  );
}

export default NuevoTurnoImagenes;
